import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Card, Table, Badge, Spinner, Alert, Button, Modal, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import PageLayout from "../../../../layouts/PageLayout";
import { apiGet, apiPost, errorText, usd, date, APPLICATION_STATUS_VARIANT } from "../../../Seller/sellerApi";

const isPdf = (url) => /\.pdf($|\?)/i.test(url || "");

function DocumentPreview({ label, url }) {
    return (
        <div className="border rounded-3 p-2 h-100">
            <div className="small fw-semibold mb-2">{label}</div>
            {!url ? (
                <span className="text-danger small">Not uploaded</span>
            ) : isPdf(url) ? (
                <a href={url} target="_blank" rel="noreferrer">Open PDF</a>
            ) : (
                <a href={url} target="_blank" rel="noreferrer">
                    <img src={url} alt={label} style={{ width: "100%", maxHeight: 260, objectFit: "contain" }} />
                </a>
            )}
        </div>
    );
}

export default function SellerApplicationDetail() {
    const { id } = useParams();
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [reason, setReason] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiGet(`/admin/seller-applications/${id}`);
            setApp(res.data?.data);
        } catch (e) {
            setError(errorText(e));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const approve = async () => {
        const ok = await Swal.fire({
            icon: "question",
            title: "Approve this seller?",
            text: "Their listings go to product review and they get an email with the seller portal link.",
            showCancelButton: true,
            confirmButtonText: "Approve",
        });
        if (!ok.isConfirmed) return;
        setBusy(true);
        try {
            const res = await apiPost(`/admin/seller-applications/${id}/approve`);
            setApp(res.data?.data);
            Swal.fire({ icon: "success", text: res.data?.message });
        } catch (e) {
            Swal.fire({ icon: "error", text: errorText(e) });
        } finally {
            setBusy(false);
        }
    };

    const reject = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await apiPost(`/admin/seller-applications/${id}/reject`, { reason });
            setApp(res.data?.data);
            setShowReject(false);
            Swal.fire({ icon: "success", text: res.data?.message });
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <PageLayout><div className="text-center py-5"><Spinner animation="border" /></div></PageLayout>;
    if (error || !app) return <PageLayout><Alert variant="danger">{error || "Application not found."}</Alert></PageLayout>;

    const p = app.profile || {};
    const local = app.region === "local";
    const storage = (app.storage_options || []).find((o) => o.value === app.storage_preference);

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">
                                {p.name} <Badge bg={APPLICATION_STATUS_VARIANT[app.account_status] || "secondary"}>{app.account_status}</Badge>
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="/admin/seller-applications" className="mc-breadcrumb-link">Seller Applications</Link></li>
                                <li className="mc-breadcrumb-item">#{app.id}</li>
                            </ul>
                        </div>
                        {app.account_status === "pending" && (
                            <div className="d-flex gap-2">
                                <Button variant="danger" disabled={busy} onClick={() => setShowReject(true)}>Reject</Button>
                                <Button variant="success" disabled={busy} onClick={approve}>Approve seller</Button>
                            </div>
                        )}
                        {app.account_status === "approved" && (
                            <Button as={Link} to={`/admin/store-sellers/${app.user_id}`} variant="primary">Open store seller</Button>
                        )}
                    </div>
                </Col>

                {app.rejection_reason && (
                    <Col xl={12}><Alert variant="warning">Last rejection reason: {app.rejection_reason}</Alert></Col>
                )}
                {app.missing?.length > 0 && app.account_status !== "approved" && (
                    <Col xl={12}>
                        <Alert variant="secondary">
                            <strong>Not complete:</strong> {app.missing.join(" ")}
                        </Alert>
                    </Col>
                )}

                <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">Profile</h5>
                            <Table size="sm" className="mb-0">
                                <tbody>
                                    <tr><th>Name</th><td>{p.name} {p.nickname ? <span className="text-muted">({p.nickname})</span> : null}</td></tr>
                                    <tr><th>Father&apos;s name</th><td>{p.father_name || "-"}</td></tr>
                                    <tr><th>Email</th><td>{p.email} {p.email_verified ? <Badge bg="success">Verified {date(p.email_verified_at)}</Badge> : <Badge bg="danger">Not verified</Badge>}</td></tr>
                                    <tr><th>Phone</th><td>{p.phone || "-"} {p.phone_verified ? <Badge bg="success">Verified</Badge> : <Badge bg="secondary">Not verified</Badge>}</td></tr>
                                    <tr><th>Country</th><td>{app.country} ({local ? "Pakistan workflow" : "International workflow"})</td></tr>
                                    <tr><th>Submitted</th><td>{date(app.submitted_at)}</td></tr>
                                </tbody>
                            </Table>
                            <h6 className="fw-bold mt-3">Addresses</h6>
                            {(app.addresses || []).map((a) => (
                                <div key={a.id} className={`small border rounded p-2 mb-1 ${a.selected ? "border-dark" : ""}`}>
                                    {a.selected && <Badge bg="dark" className="me-1">Seller address</Badge>}
                                    {[a.address, a.city, a.country].filter(Boolean).join(", ")}
                                </div>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">Payment methods</h5>
                            {(app.payment_methods || []).length === 0 && <p className="text-muted">None added.</p>}
                            {(app.payment_methods || []).map((m) => (
                                <div key={m.id} className="border rounded-3 p-2 mb-2">
                                    <strong>{m.label}</strong> {m.is_default && <Badge bg="primary">Default</Badge>}
                                    <Table size="sm" borderless className="mb-0 small">
                                        <tbody>
                                            {Object.entries(m.details || {}).map(([k, v]) => (
                                                <tr key={k}><td className="text-muted" style={{ width: "45%" }}>{m.detail_labels?.[k] || k}</td><td>{v || "-"}</td></tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ))}
                            <h5 className="fw-bold mt-4 mb-2">Storage preference</h5>
                            {storage ? (
                                <p className="mb-0"><strong>{storage.label}</strong> · {storage.fee_percent}% {storage.value === "own_store" ? "platform fee" : "commission"}</p>
                            ) : <p className="text-muted mb-0">Not chosen.</p>}
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">Verification documents</h5>
                            {local ? (
                                <>
                                    <p className="small text-muted">Pakistani seller: CNIC front and back. No bank statement, utility bill or payment-method verification needed.</p>
                                    {app.documents?.cnic && <p>CNIC number: <strong>{app.documents.cnic}</strong></p>}
                                    <Row className="g-3">
                                        <Col md={6}><DocumentPreview label="CNIC front" url={app.documents?.cnic_front_image} /></Col>
                                        <Col md={6}><DocumentPreview label="CNIC back" url={app.documents?.cnic_back_image} /></Col>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    <p className="small text-muted">International seller: latest bank statement with the bank stamp, or a utility bill in the seller&apos;s own name.</p>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <DocumentPreview
                                                label={app.documents?.verification_document_type === "bank_statement" ? "Bank statement" : app.documents?.verification_document_type === "utility_bill" ? "Utility bill" : "Document"}
                                                url={app.documents?.verification_document}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body>
                            <h5 className="fw-bold mb-3">Listed watches</h5>
                            <Table responsive className="align-middle mb-0">
                                <thead><tr><th></th><th>Watch</th><th>Brand / category</th><th>Price</th><th>Status</th></tr></thead>
                                <tbody>
                                    {(app.products || []).map((pr) => (
                                        <tr key={pr.id}>
                                            <td style={{ width: 80 }}>{pr.card_img_url && <img src={pr.card_img_url} alt="" style={{ width: 64, height: 64, objectFit: "cover" }} className="rounded" />}</td>
                                            <td><strong>{pr.title}</strong><small className="d-block text-muted">{pr.product_code}</small></td>
                                            <td>{[pr.brand, pr.category].filter(Boolean).join(" / ")}</td>
                                            <td>{usd(pr.sell_price_dollar)}<small className="d-block text-muted">+ {usd(pr.shipping_charges_dollar)} shipping</small></td>
                                            <td><Badge bg={pr.is_draft ? "secondary" : "info"}>{pr.is_draft ? "Draft" : [pr.status, pr.status_2].filter(Boolean).join(" / ")}</Badge></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {app.account_status === "approved" && (
                                <p className="small text-muted mt-2 mb-0">Approved listings are reviewed in <Link to="/admin/seller-products">Seller Products</Link>.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showReject} onHide={() => setShowReject(false)} centered>
                <Form onSubmit={reject}>
                    <Modal.Header closeButton><Modal.Title>Reject application</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <Form.Label htmlFor="reject-reason">Reason (sent to the seller by email)</Form.Label>
                        <Form.Control id="reject-reason" as="textarea" rows={4} required value={reason} onChange={(e) => setReason(e.target.value)} />
                        <Form.Text>The seller can fix the application and submit it again.</Form.Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowReject(false)}>Cancel</Button>
                        <Button type="submit" variant="danger" disabled={busy || !reason.trim()}>Reject</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}
